import './MultiSkuConfigWizard.less';

import {
  Affix,
  Divider,
  Modal,
  PageHeader,
  Progress,
  Steps,
  Tooltip,
} from 'antd';
import Text from 'antd/lib/typography/Text';
import {
  ArrowLeftIcon,
  DoneCircleIcon,
  UncheckedCircleIcon,
} from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { Headline } from 'components/RevxTypography';
import { PartialSKU, StepActionTypeEnum } from 'domains/skuDomain';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ADMIN_DETAIL_AFFIX_TOP } from 'utils/constants';
// import { useAppSelector } from 'app/hooks';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxSpin } from 'components/RevxSpin';
import { useSkuSvcRole } from 'hooks/useSkuSvcRole';
import skuService from 'services/skuService';
import MultiWizardStep from './MultiWizardStep';
import { withMeasurementUnitScope } from 'hooks/useMeasurementUnitScope';
import { RevxUnitSelect } from 'components/RevxUnitSelect';

const { Step } = Steps;
type Props = {
  onCancel: () => void;
  sku: PartialSKU;
};
// const totalStep = 5;

const { confirm } = Modal;

const MultiSkuConfigWizard = ({ onCancel, sku }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const multiStep = useAppSelector((state) => state.sku.multiStep);
  const [loading, setLoading] = useState<boolean>(false);
  const stepAction = useAppSelector((state) => state.sku.stepAction);
  const stepActionPayload = useAppSelector(
    (state) => state.sku.stepActionPayload
  );
  const svcRole = useSkuSvcRole(sku);
  const stepType: StepActionTypeEnum[] = useMemo(() => {
    let returnData = [
      StepActionTypeEnum.STEP0_OVERVIEW,
      StepActionTypeEnum.STEP2_SIZES,
    ];
    if (svcRole.isJobOption) {
      returnData.push(StepActionTypeEnum.STEP7_JOBOPTIONS);
    }
    if (svcRole.isAdditionalJobOption) {
      returnData.push(StepActionTypeEnum.STEP8_ADDITIONNALJOBOPTIONS);
    }
    return [...returnData, StepActionTypeEnum.STEP1_QUANTITIES];
  }, [svcRole]);

  const totalStep = useMemo(() => {
    let returnData = 3;
    if (svcRole.isJobOption) {
      returnData++;
    }
    if (svcRole.isAdditionalJobOption) {
      returnData++;
    }
    return returnData;
  }, [svcRole]);

  const confirmCancel = () => {
    if (sku.readonly) {
      onCancel();
      dispatch.sku.setMultiStep(0);
    } else {
      confirm({
        title: 'Are you sure exit without saving?',
        okText: 'Confirm',
        cancelText: 'Save',
        centered: true,
        onOk() {
          return new Promise(async (resolve, reject) => {
            onCancel();
            dispatch.sku.setMultiStep(0);
            resolve('success');
          });
        },
        onCancel() {
          return new Promise(async (resolve, reject) => {
            await doStepAction();
            onCancel();
            dispatch.sku.setMultiStep(0);
            resolve('success');
          });
        },
      });
    }
  };

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            confirmCancel();
          }}
          backIcon={<ArrowLeftIcon />}
          title={<Headline>{sku?.name}</Headline>}
          subTitle={<RevxUnitSelect />}
          extra={[
            <>
              <RevxButton
                onClick={async () => {
                  setLoading(true);
                  const skuDraft = await skuService.startEditSku(sku);
                  dispatch.sku.setState({ curItem: skuDraft });
                  setLoading(false);
                }}
                brandGreen
                key="refreshTip"
              >
                <Tooltip title={t('platform.sku.wizard.refreshTip')}>
                  {t('common.refresh')}
                </Tooltip>
              </RevxButton>
              <RevxButton onClick={confirmCancel} brandGreen key="cancel">
                {t('common.cancel')}
              </RevxButton>
            </>,
          ]}
        ></PageHeader>
      </Affix>
    </div>
  );
  // const [percent, setPercent] = useState(0);

  const percent = useMemo(() => {
    if (multiStep >= totalStep) {
      return 100;
    }
    return Math.ceil((multiStep * 100) / totalStep);
  }, [multiStep, totalStep]);

  const doStepAction = async () => {
    setLoading(true);
    if (stepAction) {
      if (stepType[multiStep] && stepAction[stepType[multiStep]]) {
        const payload = stepActionPayload
          ? stepActionPayload[stepType[multiStep]]
          : {};
        const res = await stepAction[stepType[multiStep]](payload);
        if (!res) {
          setLoading(false);
          throw new Error('step error');
        }
      }
    }
    setLoading(false);
  };

  return (
    <RevxSpin spinning={loading}>
      <div className="multi-sku-config-wizard-wrapper">
        {actionBar}
        <div className="multi-sku-config-wizard">
          <RevxFlexLayout align="top">
            <div className="multi-sku-config-wizard-left">
              <div className="multi-sku-config-wizard-progress-panel">
                <div className="multi-sku-config-wizard-title">
                  <span className="brand-green">
                    {t('platform.sku.wizard.configuration')}
                  </span>{' '}
                  {t('platform.sku.wizard.options')}
                </div>

                <div style={{ marginRight: 0 }}>
                  <Progress
                    percent={percent}
                    status={multiStep < totalStep ? 'exception' : 'active'}
                    showInfo={false}
                  />
                </div>
                <Text>
                  {percent}% {t('platform.sku.wizard.complete')}
                </Text>
                <RevxButton
                  brandGreen
                  className="step-btn"
                  onClick={async () => {
                    await doStepAction();

                    if (multiStep + 1 >= totalStep) {
                      dispatch.sku.setMultiStep(0);
                      onCancel();
                      return;
                    }
                    dispatch.sku.setMultiStep(multiStep + 1);
                  }}
                >
                  {t('platform.sku.wizard.nextStep')}
                </RevxButton>
              </div>
              <Divider type="horizontal" />

              <Steps
                direction="vertical"
                current={multiStep}
                progressDot={(iconDot, { index, status }) => {
                  return status === 'process' ? (
                    <DoneCircleIcon />
                  ) : index < multiStep ? (
                    <DoneCircleIcon />
                  ) : (
                    <UncheckedCircleIcon />
                  );
                }}
                onChange={async (step) => {
                  try {
                    await doStepAction();

                    if (step >= totalStep) {
                      onCancel();
                    } else {
                      dispatch.sku.setMultiStep(step);
                    }
                  } catch {}
                }}
              >
                <Step title={'Initial Overview Section'} />
                <Step
                  title={t('platform.sku.form.label.sizes')}
                  description={`${t('platform.sku.wizard.whichSizes')} ${t(
                    'platform.sku.wizard.willYouSupport'
                  )}`}
                />
                {svcRole.isJobOption && (
                  <Step title={t('platform.sku.form.label.jobOptions')} />
                )}
                {svcRole.isAdditionalJobOption && (
                  <Step
                    title={t('platform.sku.form.label.additionalJobOptions')}
                  />
                )}
                <Step title={'Quantities'} />
                {/* <Step title={'Sizes'} /> */}
              </Steps>
            </div>
            <div className="multi-sku-config-wizard-right">
              <MultiWizardStep step={multiStep} sku={sku} />
            </div>
          </RevxFlexLayout>
        </div>
        ,
      </div>
    </RevxSpin>
  );
};

export default withMeasurementUnitScope(MultiSkuConfigWizard);
